import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import api from '../../services/api';
import type { CompanyBudgetProgressItem } from '../../types/companyAccountEconomy';
import type { AccountEconomyRow } from '../../types/companyAccountEconomy';
import { formatAccountMetricValue } from '../../utils/accountMetricFormat';
import { formatAccountsSectionTitle } from '../../utils/accountDashboardFocus';
import { useCompanyDashboard } from './CompanyDashboardContext';
import type { BudgetType } from '../../types/accountBudget';
import { metricFromBudgetType } from '../../types/accountTrend';

interface AccountRow {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface CompanyAccountsTableProps {
  accounts: AccountRow[];
  accountsLoading: boolean;
  canEdit: boolean;
  onOpenAccount: (accountId: string, accountName: string) => void;
  onAddAccount: () => void;
}

function budgetTypeLabel(type: BudgetType): string {
  switch (type) {
    case 'HOURS':
      return 'Hours';
    case 'INTERNAL_COST':
      return 'Internal cost';
    case 'BILLABLE':
      return 'Billable';
    default:
      return type;
  }
}

export default function CompanyAccountsTable({
  accounts,
  accountsLoading,
  canEdit,
  onOpenAccount,
  onAddAccount
}: CompanyAccountsTableProps) {
  const { companyId, accessGranted, metric, granularity, anchor, focusWindow, currencyCode } =
    useCompanyDashboard();

  const [economyRows, setEconomyRows] = useState<AccountEconomyRow[]>([]);
  const [progressByAccount, setProgressByAccount] = useState<Record<string, CompanyBudgetProgressItem>>(
    {}
  );
  const [economyLoading, setEconomyLoading] = useState(false);

  const economyVisible = accessGranted === true;
  const showRegistered = economyVisible;
  const showProgress = economyVisible && Object.keys(progressByAccount).length > 0;

  const loadEconomy = useCallback(async () => {
    if (!economyVisible) {
      setEconomyRows([]);
      setProgressByAccount({});
      return;
    }
    try {
      setEconomyLoading(true);
      const [economyRes, progressRes] = await Promise.all([
        api.getCompanyAccountEconomy(companyId, { metric, granularity, anchor }),
        api.getCompanyBudgetProgress(companyId)
      ]);

      if (economyRes.success) {
        setEconomyRows(economyRes.data.accounts ?? []);
      } else {
        setEconomyRows([]);
      }

      if (progressRes.success) {
        const map: Record<string, CompanyBudgetProgressItem> = {};
        for (const item of (progressRes.data ?? []) as CompanyBudgetProgressItem[]) {
          map[item.accountId] = item;
        }
        setProgressByAccount(map);
      } else {
        setProgressByAccount({});
      }
    } catch {
      setEconomyRows([]);
      setProgressByAccount({});
    } finally {
      setEconomyLoading(false);
    }
  }, [companyId, metric, granularity, anchor, economyVisible]);

  useEffect(() => {
    loadEconomy();
  }, [loadEconomy]);

  const economyMap = useMemo(
    () => new Map(economyRows.map((r) => [r.accountId, r])),
    [economyRows]
  );

  const sectionTitle = formatAccountsSectionTitle(focusWindow?.label, granularity);

  const formatRegistered = (value: number, rowCurrency?: string) =>
    formatAccountMetricValue(value, metric, rowCurrency || currencyCode);

  const formatBudgetValue = (item: CompanyBudgetProgressItem, value: number) => {
    const budgetMetric = metricFromBudgetType(item.type);
    return formatAccountMetricValue(value, budgetMetric, item.currencyCode ?? currencyCode);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{sectionTitle}</Typography>
        {canEdit && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAddAccount}>
            Add Account
          </Button>
        )}
      </Box>

      {accountsLoading || economyLoading ? (
        <LinearProgress />
      ) : accounts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No accounts found.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Status</TableCell>
                {showRegistered && <TableCell align="right">Registered</TableCell>}
                {showProgress && <TableCell>Budget progress</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => {
                const economy = economyMap.get(account.id);
                const progress = progressByAccount[account.id];
                const canShowRegistered = economy?.economyVisible !== false && showRegistered;

                return (
                  <TableRow
                    key={account.id}
                    hover
                    sx={{ cursor: 'pointer', opacity: account.isActive ? 1 : 0.7 }}
                    onClick={() => onOpenAccount(account.id, account.name)}
                  >
                    <TableCell>
                      <Typography variant="body2">{account.name}</Typography>
                      {economy?.accountNumber && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {economy.accountNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={account.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={account.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    {showRegistered && (
                      <TableCell align="right">
                        {canShowRegistered
                          ? formatRegistered(economy?.registered ?? 0, economy?.currencyCode)
                          : '—'}
                      </TableCell>
                    )}
                    {showProgress && (
                      <TableCell>
                        {progress && economy?.economyVisible !== false ? (
                          <Box sx={{ minWidth: 140 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Chip
                                label={budgetTypeLabel(progress.type)}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, progress.percent)}
                              color={progress.percent > 100 ? 'error' : 'primary'}
                              sx={{ mb: 0.5 }}
                            />
                            <Typography
                              variant="caption"
                              color={progress.percent > 100 ? 'error.main' : 'text.secondary'}
                            >
                              {formatBudgetValue(progress, progress.registered)} /{' '}
                              {formatBudgetValue(progress, progress.target)} (
                              {progress.percent.toFixed(0)}%)
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => onOpenAccount(account.id, account.name)}
                        aria-label="Open account"
                      >
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
